/**
 * Admin Console data access. These server functions are the single source of
 * truth for the Operator Console's Dashboard, Orders, Customers and
 * Users & Roles pages.
 *
 * This module intentionally contains nothing but imports and `createServerFn`
 * declarations. All runtime logic lives in `console.server.ts`, which each
 * handler imports at call time so production server-function splitting cannot
 * drop it from the deployed bundle.
 */
import { createServerFn } from "@tanstack/react-start";
import { requireSupabaseAuth } from "@/integrations/supabase/auth-middleware";
import type { AdminAuditRow, AdminCustomer, CloudRoleAssignment } from "./console.server";

export type { AdminAuditRow, AdminCustomer, CloudRoleAssignment };

export const listAdminOrders = createServerFn({ method: "GET" })
  .middleware([requireSupabaseAuth])
  .handler(async ({ context }) => {
    const s = await import("./console.server");
    await s.assertElevated(context as never);
    return s.fetchOrders();
  });

export const updateAdminOrderStatus = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((input: { orderId: string; status: string }) => {
    if (!input?.orderId || !input?.status) throw new Error("orderId and status required");
    return input;
  })
  .handler(async ({ data, context }) => {
    const s = await import("./console.server");
    await s.assertElevated(context as never);
    return s.updateOrderStatus(data.orderId, data.status, context.userId);
  });

export const listAdminCustomers = createServerFn({ method: "GET" })
  .middleware([requireSupabaseAuth])
  .handler(async ({ context }) => {
    const s = await import("./console.server");
    await s.assertElevated(context as never);
    return s.fetchCustomers();
  });

export const listRoleAssignmentsFn = createServerFn({ method: "GET" })
  .middleware([requireSupabaseAuth])
  .handler(async ({ context }) => {
    const s = await import("./console.server");
    await s.assertElevated(context as never);
    return s.fetchRoleAssignments();
  });

export const setCustomerBlockedFn = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator(
    (input: { email: string; userId?: string | null; blocked: boolean; reason?: string | null }) => {
      if (!input?.email) throw new Error("email required");
      if (typeof input.blocked !== "boolean") throw new Error("blocked required");
      return input;
    },
  )
  .handler(async ({ data, context }) => {
    const s = await import("./console.server");
    await s.assertElevated(context as never);
    return s.setCustomerBlocked({
      email: data.email,
      userId: data.userId ?? null,
      blocked: data.blocked,
      reason: data.reason ?? null,
      actorId: context.userId,
    });
  });

export const listAdminAuditLogFn = createServerFn({ method: "GET" })
  .middleware([requireSupabaseAuth])
  .handler(async ({ context }) => {
    const s = await import("./console.server");
    await s.assertElevated(context as never);
    return s.fetchAdminAuditLog();
  });

export const getMyRolesFn = createServerFn({ method: "GET" })
  .middleware([requireSupabaseAuth])
  .handler(async ({ context }) => {
    const { data, error } = await context.supabase
      .from("user_roles")
      .select("role")
      .eq("user_id", context.userId);
    if (error) throw new Error(error.message);
    const s = await import("./console.server");
    return s.rolesForCurrentTenant(context.userId, (data ?? []).map((r) => r.role as string));
  });

export const hasAnySuperAdminFn = createServerFn({ method: "GET" }).handler(async () => {
  const s = await import("./console.server");
  return { exists: (await s.countSuperAdmins()) > 0 };
});

/**
 * One-time bootstrap: the first authenticated user may claim Super Admin while
 * no Super Admin exists. Mirrors the existing /setup wizard contract.
 */
export const claimSuperAdminFn = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .handler(async ({ context }) => {
    const s = await import("./console.server");
    if ((await s.countSuperAdmins()) > 0) throw new Error("A Super Admin already exists.");
    return s.grantRoles(context.userId, ["super_admin", "admin"]);
  });

export const assignRoleFn = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((input: { userId: string; role: string }) => {
    if (!input?.userId || !input?.role) throw new Error("userId and role required");
    return input;
  })
  .handler(async ({ data, context }) => {
    const s = await import("./console.server");
    await s.assertElevated(context as never);
    return s.grantRoles(data.userId, [data.role]);
  });

export const removeRoleFn = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((input: { userId: string; role: string }) => {
    if (!input?.userId || !input?.role) throw new Error("userId and role required");
    return input;
  })
  .handler(async ({ data, context }) => {
    const s = await import("./console.server");
    await s.assertElevated(context as never);
    return s.revokeRole(data.userId, data.role);
  });
